import { LOGOUT } from '@comeon/actions-public';
import { keyBy, sumBy } from '@comeon/utility';
import { createSelector } from 'reselect';
import reducerRegistry from '@comeon/reducer-registry';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { selectBalance } from '~/components/account/ducks/index.js';
import { requestApi } from '../../../services/api.js';
import configurationReducer, {
	selectBetAmountTaxationRule,
	selectPollingTime,
	selectWinOverLimitTaxationRule
} from './highlight-games-configuration-duck.js';
import historyReducer from './highlight-games-history-duck.js';
import {
	REDUCER_NAME,
	ACTION_PREFIX,
	HIGHLIGHT_GAMES_BASE_URL,
	BETSLIP_VALIDATION_LOW_BALANCE,
	BETSLIP_VALIDATION_UNKNOWN_ERROR,
	BETSLIP_VALIDATION_ZERO_AMOUNT,
	PENDING_BET_STATE_PENDING,
	PENDING_BET_STATE_PLACED,
	PENDING_BET_STATE_SENDING,
	WEEK_STATUS_PREAMBLE,
	SCHEDULE_STATUS_OFFLINE,
	BETSLIP_TAB_SINGLE,
	BETSLIP_TAB_COMBO,
	BETSLIP_VALIDATION_SAME_EVENT_COMBO,
	BETSLIP_VALIDATION_UNAUTHENTICATED,
	BETSLIP_VALIDATION_ONE_LINE_COMBO,
	BETSLIP_VALIDATION_POSSIBLE_WIN_LOWER_THAN_AMOUNT
} from '../highlight-games-constants.js';
import { calculateTaxationAmounts, extendBetsWithTaxedAmountsAndPossibleWin } from '../highlight-games-utils.js';

// Constants
const BETSLIP_MESSAGE_TIMEOUT_MS = 5000;

// Actions
const SUBSCRIBED_TO_INFO = `${ACTION_PREFIX}/SUBSCRIBED_TO_INFO`;
const UNSUBSCRIBED_TO_INFO = `${ACTION_PREFIX}/UNSUBSCRIBED_TO_INFO`;

const FETCH_INFO_REQUESTED = `${ACTION_PREFIX}/FETCH_INFO_REQUESTED`;
const FETCH_INFO_SUCCEEDED = `${ACTION_PREFIX}/FETCH_INFO_SUCCEEDED`;
const FETCH_INFO_FAILED = `${ACTION_PREFIX}/FETCH_INFO_FAILED`;
const FETCH_INFO_SCHEDULE_OFFLINE = `${ACTION_PREFIX}/FETCH_INFO_SCHEDULE_OFFLINE`;

const FETCH_EVENTS_FOR_WEEK_REQUESTED = `${ACTION_PREFIX}/FETCH_EVENTS_FOR_WEEK_REQUESTED`;
const FETCH_EVENTS_FOR_WEEK_SUCCEEDED = `${ACTION_PREFIX}/FETCH_EVENTS_FOR_WEEK_SUCCEEDED`;
const FETCH_EVENTS_FOR_WEEK_FAILED = `${ACTION_PREFIX}/FETCH_EVENTS_FOR_WEEK_FAILED`;

const PENDING_BET_CREATED = `${ACTION_PREFIX}/PENDING_BET_CREATED`;
const PENDING_BET_UPDATED = `${ACTION_PREFIX}/PENDING_BET_UPDATED`;
const PENDING_BET_REMOVED = `${ACTION_PREFIX}/PENDING_BET_REMOVED`;

const PLACE_BETS_REQUESTED = `${ACTION_PREFIX}/PLACE_BETS_REQUESTED`;
const PLACE_BETS_SUCCEEDED = `${ACTION_PREFIX}/PLACE_BETS_SUCCEEDED`;
const PLACE_BETS_FAILED = `${ACTION_PREFIX}/PLACE_BETS_FAILED`;

const BETSLIP_CLEARED = `${ACTION_PREFIX}/BETSLIP_CLEARED`;

const BETSLIP_VALIDATED = `${ACTION_PREFIX}/BETSLIP_VALIDATED`;
const BETSLIP_MESSAGE_SHOWN = `${ACTION_PREFIX}/BETSLIP_MESSAGE_SHOWN`;
const BETSLIP_MESSAGE_HIDDEN = `${ACTION_PREFIX}/BETSLIP_MESSAGE_HIDDEN`;
const BETSLIP_EXPANDED = `${ACTION_PREFIX}/BETSLIP_EXPANDED`;
const BETSLIP_MINIMISED = `${ACTION_PREFIX}/BETSLIP_MINIMISED`;

const ONBOARDING_SHOWN = `${ACTION_PREFIX}/ONBOARDING_SHOWN`;
const ONBOARDING_HIDDEN = `${ACTION_PREFIX}/ONBOARDING_HIDDEN`;
const ONBOARDING_DISABLED_UPDATED = `${ACTION_PREFIX}/ONBOARDING_DISABLED_UPDATED`;

const NANOPLAYER_SCRIPTS_LOADED = `${ACTION_PREFIX}/NANOPLAYER_SCRIPTS_LOADED`;
const VIDEO_MUTED = `${ACTION_PREFIX}/VIDEO_MUTED`;
const VIDEO_UNMUTED = `${ACTION_PREFIX}/VIDEO_UNMUTED`;
const ACTIVE_BETSLIP_TAB_UPDATED = `${ACTION_PREFIX}/ACTIVE_BETSLIP_TAB_UPDATED`;
const COMBO_BET_AMOUNTS_UPDATED = `${ACTION_PREFIX}/COMBO_BET_AMOUNTS_UPDATED`;

// Selectors
export const selectCurrentWeekNumber = state => state[REDUCER_NAME].currentWeekNumber;
export const selectCurrentWeekStatus = state => state[REDUCER_NAME].currentWeekStatus;
export const selectEventIdsByWeek = state => state[REDUCER_NAME].eventIdsByWeek;
export const selectEventsById = state => state[REDUCER_NAME].eventsById;
export const selectCurrentWeekEvents = createSelector(
	[selectCurrentWeekNumber, selectEventsById, selectEventIdsByWeek],
	(currentWeekNumber, eventsById, eventIdsByWeek) =>
		eventIdsByWeek[currentWeekNumber] ? eventIdsByWeek[currentWeekNumber].map(eventId => eventsById[eventId]) : []
);
export const selectScheduleStatus = state => state[REDUCER_NAME].scheduleStatus;
export const selectBarePendingBets = state => state[REDUCER_NAME].pendingBets;
export const selectPendingBets = createSelector([selectBarePendingBets, selectEventsById], (pendingBets, events) =>
	pendingBets.map(pendingBet => {
		const event = events[pendingBet.eventId];
		const market = event.markets.find(eventMarket => eventMarket.id === pendingBet.marketId);
		const selection = market.selections.find(marketSelection => marketSelection.id === pendingBet.selectionId);
		return {
			...pendingBet,
			event,
			market,
			selection
		};
	})
);
export const selectPendingBetsState = state => state[REDUCER_NAME].pendingBetsState;
export const selectActiveSelectionIds = createSelector(
	[selectBarePendingBets, selectPendingBetsState],
	(pendingBets, pendingBetsState) =>
		pendingBetsState === PENDING_BET_STATE_PENDING ? pendingBets.map(pendingBet => pendingBet.selectionId) : []
);

const selectBetslipMessage = state => state[REDUCER_NAME].betslipMessage;
export const selectBetslipIsExpanded = state => state[REDUCER_NAME].isBetslipExpanded;
const selectBetslipErrors = state => state[REDUCER_NAME].betslipErrors;
export const selectBetslipValidation = createSelector(
	[selectBetslipMessage, selectBetslipErrors],
	(message, errors) => message || errors[0] || null
);

export const selectCanPlaceBets = state => state[REDUCER_NAME].betslipErrors.length === 0;

export const selectEventById = id => createSelector([selectEventsById], eventsById => eventsById[id]);

export const selectPendingBetsPrice = createSelector([selectPendingBets], pendingBets => {
	return pendingBets.map(pendingBet => pendingBet.selection.price).reduce((sum, price) => sum * price, 1);
});

export const selectIsOnboardingOpen = state => state[REDUCER_NAME].isOnboardingOpen;
export const selectIsOnboardingDisabled = state => state[REDUCER_NAME].isOnboardingDisabled;
export const selectNumberOfPendingBets = state => state[REDUCER_NAME].pendingBets.length;
export const selectExternalNanoplayerScriptHasLoaded = state => state[REDUCER_NAME].externalNanoplayerScriptHasLoaded;
export const selectIsVideoMuted = state => state[REDUCER_NAME].isVideoMuted;
export const selectShowServerOfflinePage = state => state[REDUCER_NAME].showServerOfflinePage;

export const selectActiveBetslipTab = state => state[REDUCER_NAME].activeBetslipTab;
export const selectComboBetAmounts = state => state[REDUCER_NAME].comboBetAmounts;
export const selectComboBetAmount = state => selectComboBetAmounts(state).amount;
export const selectComboBetPrice = state =>
	selectPendingBets(state)
		.map(pendingBet => pendingBet.selection.price)
		.reduce((sum, price) => sum * price, 1);

export const selectPendingBetsTotalAmount = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE ? sumBy(pendingBets, 'amount') : comboBetAmounts.amount
);
export const selectPendingBetsTotalAmountAfterTaxation = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE
			? sumBy(pendingBets, 'amountAfterTaxation')
			: comboBetAmounts.amountAfterTaxation
);
export const selectPendingBetsTotalAmountTaxation = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE ? sumBy(pendingBets, 'amountTaxation') : comboBetAmounts.amountTaxation
);
export const selectPendingBetsTotalReturnAmount = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE ? sumBy(pendingBets, 'returnAmount') : comboBetAmounts.returnAmount
);
export const selectPendingBetsTotalReturnAmountBeforeTaxation = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE
			? sumBy(pendingBets, 'returnAmountBeforeTaxation')
			: comboBetAmounts.returnAmountBeforeTaxation
);
export const selectPendingBetsTotalReturnAmountTaxation = createSelector(
	[selectBarePendingBets, selectComboBetAmounts, selectActiveBetslipTab],
	(pendingBets, comboBetAmounts, activeTab) =>
		activeTab === BETSLIP_TAB_SINGLE
			? sumBy(pendingBets, 'returnAmountTaxation')
			: comboBetAmounts.returnAmountTaxation
);

const selectPendingBetEventIds = createSelector([selectBarePendingBets], pendingBets =>
	pendingBets.map(pendingBet => pendingBet.eventId)
);

export const selectBetReceipts = state => state[REDUCER_NAME].betReceipts;

const selectHasAnyZeroBetAmounts = createSelector(
	[selectActiveBetslipTab, selectBarePendingBets, selectPendingBetsTotalAmount],
	(activeTab, pendingBets, totalBetAmount) =>
		(activeTab === BETSLIP_TAB_SINGLE && pendingBets.some(pendingBet => pendingBet.amount === 0)) ||
		(activeTab === BETSLIP_TAB_COMBO && totalBetAmount === 0)
);

// Helper Functions
const isBettingAllowedForStatus = weekStatus => weekStatus === WEEK_STATUS_PREAMBLE;

const isComboBetAllowed = eventIds => {
	const uniqueIds = eventIds.filter((x, i, a) => a.indexOf(x) === i);

	return eventIds.length === uniqueIds.length;
};

// Action creators
export const closeOnboarding = () => ({ type: ONBOARDING_HIDDEN });

export const openOnboarding = () => ({ type: ONBOARDING_SHOWN });

export const muteVideo = () => ({ type: VIDEO_MUTED });

export const unmuteVideo = () => ({ type: VIDEO_UNMUTED });

export const validateBetslipState = () => (dispatch, getState) => {
	const errors = [];

	const activeTab = selectActiveBetslipTab(getState());
	const pendingBets = selectBarePendingBets(getState());

	// Validate combo selections
	if (activeTab === BETSLIP_TAB_COMBO) {
		const eventIds = selectPendingBetEventIds(getState());
		if (!isComboBetAllowed(eventIds)) {
			errors.push({ type: BETSLIP_VALIDATION_SAME_EVENT_COMBO });
		}

		if (pendingBets.length < 2) {
			errors.push({ type: BETSLIP_VALIDATION_ONE_LINE_COMBO });
		}
	}

	// Validate betslip amount and balance
	const totalPendingBetAmount = selectPendingBetsTotalAmount(getState());
	const balance = selectBalance(getState());
	if (totalPendingBetAmount > balance) {
		errors.push({ type: BETSLIP_VALIDATION_LOW_BALANCE });
	}

	// Validate authentication state
	const isAuthenticated = authenticationSelector(getState());
	if (!isAuthenticated) {
		errors.push({ type: BETSLIP_VALIDATION_UNAUTHENTICATED });
	}

	// Validate Possible Win lower than amount
	if (activeTab === BETSLIP_TAB_SINGLE) {
		pendingBets.forEach(pendingBet => {
			if (pendingBet.amount > 0 && pendingBet.returnAmount <= pendingBet.amount) {
				errors.push({
					type: BETSLIP_VALIDATION_POSSIBLE_WIN_LOWER_THAN_AMOUNT
				});
			}
		});
	} else if (activeTab === BETSLIP_TAB_COMBO) {
		const comboBetAmounts = selectComboBetAmounts(getState());
		if (totalPendingBetAmount > 0 && comboBetAmounts.returnAmount <= totalPendingBetAmount) {
			errors.push({
				type: BETSLIP_VALIDATION_POSSIBLE_WIN_LOWER_THAN_AMOUNT
			});
		}
	}

	// Update errors
	dispatch({ type: BETSLIP_VALIDATED, payload: { errors } });

	// Remove corrected messages
	const currentBetslipMessage = selectBetslipMessage(getState());
	const hasZeroAmounts = selectHasAnyZeroBetAmounts(getState());
	if (currentBetslipMessage && currentBetslipMessage.type === BETSLIP_VALIDATION_ZERO_AMOUNT && !hasZeroAmounts) {
		dispatch({ type: BETSLIP_MESSAGE_HIDDEN });
	}
};

export const clearBetslip = () => dispatch => {
	dispatch({ type: BETSLIP_CLEARED });
	dispatch(validateBetslipState());
};

export const setActiveBetslipTab = activeTab => dispatch => {
	dispatch({
		type: ACTIVE_BETSLIP_TAB_UPDATED,
		payload: { activeTab }
	});
	dispatch(validateBetslipState());
};

export const handleExpandBetslip = () => ({ type: BETSLIP_EXPANDED });
export const handleMinimiseBetslip = () => ({ type: BETSLIP_MINIMISED });

export const minimiseBetslip = handleMinimiseBetslip;

/**
 * Expand the betslip
 */
export const expandBetslip = handleExpandBetslip;
export const toggleBetslip = () => (dispatch, getState) => {
	const isEpxanded = selectBetslipIsExpanded(getState());
	dispatch(isEpxanded ? minimiseBetslip() : expandBetslip());
};

export const fetchEventsForWeek = week => {
	return async dispatch => {
		dispatch({ type: FETCH_EVENTS_FOR_WEEK_REQUESTED, payload: { week } });

		try {
			const response = await requestApi(`${HIGHLIGHT_GAMES_BASE_URL}/week/${week}/events`);

			dispatch({
				type: FETCH_EVENTS_FOR_WEEK_SUCCEEDED,
				payload: { events: response, week }
			});
		} catch (error) {
			dispatch({ type: FETCH_EVENTS_FOR_WEEK_FAILED, week });
			throw new Error(error);
		}
	};
};

export const setOnboardingDisabled = onboardingIsDisabled => ({
	type: ONBOARDING_DISABLED_UPDATED,
	payload: { onboardingIsDisabled }
});

const pollInfo = () => async (dispatch, getState) => {
	const state = getState();
	const { isPolling, currentWeekNumber, currentWeekStatus } = state[REDUCER_NAME];

	if (!isPolling) {
		return;
	}

	dispatch({ type: FETCH_INFO_REQUESTED });

	try {
		const response = await requestApi(`${HIGHLIGHT_GAMES_BASE_URL}/info`);

		dispatch({
			type: FETCH_INFO_SUCCEEDED,
			payload: {
				currentWeekNumber: response.gameWeek,
				currentWeekStatus: response.currentWeekStatus,
				scheduleStatus: response.scheduleStatus
			}
		});

		if (
			response.gameWeek !== currentWeekNumber ||
			(response.gameWeek === currentWeekNumber &&
				response.currentWeekStatus === WEEK_STATUS_PREAMBLE &&
				currentWeekStatus !== WEEK_STATUS_PREAMBLE)
		) {
			dispatch(validateBetslipState());
			dispatch(fetchEventsForWeek(response.gameWeek));
		}

		if (response.scheduleStatus === SCHEDULE_STATUS_OFFLINE) {
			dispatch({ type: FETCH_INFO_SCHEDULE_OFFLINE });
		}
	} catch (error) {
		dispatch({ type: FETCH_INFO_FAILED });
	}

	const pollingTime = selectPollingTime(getState());
	setTimeout(() => dispatch(pollInfo()), pollingTime);
};

export const subscribeToCurrentWeekEvents = () => dispatch => {
	dispatch({ type: SUBSCRIBED_TO_INFO });
	dispatch(pollInfo());
};

export const unsubscribeToCurrentWeekEvents = () => dispatch => {
	dispatch({ type: UNSUBSCRIBED_TO_INFO });
};

export const setComboBetAmount = amount => (dispatch, getState) => {
	const comboBetPrice = selectComboBetPrice(getState());
	const betAmountTaxationRule = selectBetAmountTaxationRule(getState());
	const winOverLimitTaxationRule = selectWinOverLimitTaxationRule(getState());
	const amounts = calculateTaxationAmounts(amount, comboBetPrice, betAmountTaxationRule, winOverLimitTaxationRule);

	dispatch({
		type: COMBO_BET_AMOUNTS_UPDATED,
		payload: { comboBetAmounts: amounts }
	});

	dispatch(validateBetslipState());
};

export const togglePendingBetForSelectionId = selectionId => (dispatch, getState) => {
	const events = selectCurrentWeekEvents(getState());
	const pendingBetsState = selectPendingBetsState(getState());
	const currentWeekStatus = selectCurrentWeekStatus(getState());
	const prevNumberOfPendingBets = selectNumberOfPendingBets(getState());

	// If we are sending bets right now, do nothing
	// If we are not in preamble, do nothing
	if (pendingBetsState === PENDING_BET_STATE_SENDING || currentWeekStatus !== WEEK_STATUS_PREAMBLE) {
		return;
	}

	// If we have only have sent bets, clear bets then add selections
	if (pendingBetsState === PENDING_BET_STATE_PLACED) {
		dispatch(clearBetslip());
	}

	// If the selection is already pending, remove it, otherwise add it
	const pendingBets = selectBarePendingBets(getState());
	const existingPendingBet = pendingBets.find(pendingBet => pendingBet.selectionId === selectionId);

	if (existingPendingBet) {
		// Remove the bet if it is existing
		dispatch({
			type: PENDING_BET_REMOVED,
			payload: { pendingBet: existingPendingBet }
		});
	} else {
		// Add the bet if it is not existing
		const event = events.find(e =>
			e.markets.some(market => market.selections.some(selection => selection.id === selectionId))
		);
		const market = event.markets.find(eventMarket =>
			eventMarket.selections.some(selection => selection.id === selectionId)
		);
		if (event !== null) {
			dispatch({
				type: PENDING_BET_CREATED,
				payload: {
					pendingBet: {
						selectionId,
						eventId: event.id,
						marketId: market.id,
						amount: 0,
						amountAfterTaxation: 0,
						amountTaxation: 0,
						returnAmount: 0,
						returnAmountBeforeTaxation: 0,
						returnAmountTaxation: 0
					}
				}
			});
		}
	}

	// Refresh the combo bet amount to make sure we recalculate possible wins etc
	const comboBetAmount = selectComboBetAmount(getState());
	dispatch(setComboBetAmount(comboBetAmount));

	dispatch(validateBetslipState());
	const numberOfPendingBets = selectNumberOfPendingBets(getState());

	// Expand or minimise betslip based on numberOfPendingBets changes
	if (prevNumberOfPendingBets === 0 && numberOfPendingBets > 0) {
		dispatch(expandBetslip());
	} else if (prevNumberOfPendingBets > 0 && numberOfPendingBets === 0) {
		dispatch(minimiseBetslip());
	} else if (prevNumberOfPendingBets === 1 && numberOfPendingBets > 1) {
		dispatch(minimiseBetslip());
	} else if (prevNumberOfPendingBets > 1 && numberOfPendingBets === 1) {
		dispatch(expandBetslip());
	}
};

export const hideBetslipMessage = () => dispatch => {
	dispatch({ type: BETSLIP_MESSAGE_HIDDEN });
	dispatch(validateBetslipState());
};

export const showBetslipMessage =
	(type, text = null, autoHide = true, duration = BETSLIP_MESSAGE_TIMEOUT_MS) =>
	async dispatch => {
		dispatch({
			type: BETSLIP_MESSAGE_SHOWN,
			payload: { type, text }
		});

		if (autoHide) {
			setTimeout(() => {
				dispatch(hideBetslipMessage());
			}, duration);
		}
	};

export const updatePendingBet = updatedPendingBet => (dispatch, getState) => {
	const pendingBets = selectBarePendingBets(getState());
	const currentPendingBet = pendingBets.find(pendingBet => pendingBet.selectionId === updatedPendingBet.selectionId);

	// Update the existing bet
	const betAmountTaxationRule = selectBetAmountTaxationRule(getState());
	const winOverLimitTaxationRule = selectWinOverLimitTaxationRule(getState());
	const amounts = calculateTaxationAmounts(
		updatedPendingBet.amount,
		updatedPendingBet.selection.price,
		betAmountTaxationRule,
		winOverLimitTaxationRule
	);

	dispatch({
		type: PENDING_BET_UPDATED,
		payload: {
			pendingBet: {
				...currentPendingBet,
				...amounts
			}
		}
	});

	dispatch(validateBetslipState());
};

const handlePlacePendingBetError =
	(errorMessage = null) =>
	(dispatch, getState) => {
		dispatch({ type: PLACE_BETS_FAILED });
		dispatch(showBetslipMessage(BETSLIP_VALIDATION_UNKNOWN_ERROR, errorMessage, false));

		const currentWeekStatus = selectCurrentWeekStatus(getState());

		if (!isBettingAllowedForStatus(currentWeekStatus)) {
			dispatch(clearBetslip());
		}
	};

export const placeAllPendingBets = () => async (dispatch, getState) => {
	const pendingBets = selectBarePendingBets(getState());
	const activeTab = selectActiveBetslipTab(getState());
	const totalBetAmount = selectPendingBetsTotalAmount(getState());
	const hasZeroAmounts = selectHasAnyZeroBetAmounts(getState());

	// Just return if there are no pending bets
	// Maybe it can happen if player attempts to place during transition animation of betslip?
	if (pendingBets.length <= 0) {
		return;
	}

	// Validate if user has zero as any amount
	if (hasZeroAmounts) {
		dispatch(showBetslipMessage(BETSLIP_VALIDATION_ZERO_AMOUNT));
		return;
	}

	try {
		dispatch({ type: PLACE_BETS_REQUESTED });
		const payload =
			activeTab === BETSLIP_TAB_SINGLE
				? {
						bets: pendingBets.map(pendingBet => {
							return {
								betLines: [pendingBet],
								amount: pendingBet.amount
							};
						})
					}
				: { bets: [{ betLines: pendingBets, amount: totalBetAmount }] };
		const response = await requestApi(`${HIGHLIGHT_GAMES_BASE_URL}/bets`, payload, 'POST');

		if (!response.error) {
			const betReceipts = extendBetsWithTaxedAmountsAndPossibleWin(
				response,
				selectBetAmountTaxationRule(getState()),
				selectWinOverLimitTaxationRule(getState())
			);

			dispatch({ type: PLACE_BETS_SUCCEEDED, payload: betReceipts });
			dispatch(validateBetslipState({}));
			return;
		}

		dispatch(handlePlacePendingBetError(response.message));
	} catch (error) {
		dispatch(handlePlacePendingBetError());
		throw new Error(error);
	}
};

export const setExternalNanoplayerScriptAsLoaded = () => ({
	type: NANOPLAYER_SCRIPTS_LOADED
});

const initialState = {
	isPolling: false,
	currentWeekNumber: undefined,
	currentWeekStatus: undefined,
	scheduleStatus: undefined,
	eventIdsByWeek: {},
	eventsById: {},
	pendingBets: [],
	pendingBetsState: PENDING_BET_STATE_PENDING,
	betslipErrors: [],
	betslipMessage: null,
	isOnboardingOpen: undefined,
	isOnboardingDisabled: undefined,
	externalNanoplayerScriptHasLoaded: false,
	isVideoMuted: true,
	showServerOfflinePage: false,
	activeBetslipTab: BETSLIP_TAB_SINGLE,
	comboBetAmounts: calculateTaxationAmounts(0, 1),
	isBetslipExpanded: false
};

const reducer = (fullState = initialState, action) => {
	const { type, payload } = action;
	// Reduce all slices
	const state = {
		...fullState,
		configuration: configurationReducer(fullState.configuration, action),
		history: historyReducer(fullState.history, action)
	};
	// Reduce the core
	switch (type) {
		case SUBSCRIBED_TO_INFO:
			return { ...state, isPolling: true };
		case UNSUBSCRIBED_TO_INFO:
			return { ...state, isPolling: false };
		case FETCH_INFO_SUCCEEDED: {
			const shouldClear =
				state.pendingBetsState === PENDING_BET_STATE_PENDING &&
				((isBettingAllowedForStatus(state.currentWeekStatus) &&
					!isBettingAllowedForStatus(payload.currentWeekStatus)) ||
					payload.currentWeekNumber !== state.currentWeekNumber);
			return {
				...state,
				showServerOfflinePage: false,
				currentWeekNumber: payload.currentWeekNumber,
				currentWeekStatus: payload.currentWeekStatus,
				scheduleStatus: payload.scheduleStatus,
				// reset bets if pending and (moving away from preamble or changing week)
				pendingBets: shouldClear ? [] : state.pendingBets,
				comboBetAmounts: shouldClear ? calculateTaxationAmounts(0, 1) : state.comboBetAmounts,
				betslipMessage: shouldClear ? null : state.betslipMessage
			};
		}
		case FETCH_EVENTS_FOR_WEEK_SUCCEEDED:
			return {
				...state,
				eventsById: {
					...state.eventsById,
					...keyBy(payload.events, 'id')
				},
				eventIdsByWeek: {
					...state.eventIdsByWeek,
					[payload.week]: payload.events.map(event => event.id)
				}
			};
		case PENDING_BET_CREATED:
			return {
				...state,
				pendingBets: [...state.pendingBets, payload.pendingBet],
				pendingBetsState: PENDING_BET_STATE_PENDING,
				activeBetslipTab: state.pendingBets.length <= 0 ? BETSLIP_TAB_SINGLE : BETSLIP_TAB_COMBO,
				betslipMessage: null
			};
		case PENDING_BET_UPDATED:
			return {
				...state,
				pendingBets: state.pendingBets.map(pendingBet =>
					pendingBet.selectionId === payload.pendingBet.selectionId ? payload.pendingBet : pendingBet
				),
				betslipMessage: null
			};
		case PENDING_BET_REMOVED:
			return {
				...state,
				pendingBets: [...state.pendingBets].filter(
					pendingBet => pendingBet.selectionId !== payload.pendingBet.selectionId
				),
				activeBetslipTab: state.pendingBets.length <= 2 ? BETSLIP_TAB_SINGLE : BETSLIP_TAB_COMBO,
				comboBetAmounts:
					state.pendingBets.length === 1 ? calculateTaxationAmounts(0, 1) : state.comboBetAmounts,
				betslipMessage: null
			};
		case PLACE_BETS_REQUESTED:
			return {
				...state,
				pendingBetsState: PENDING_BET_STATE_SENDING
			};
		case PLACE_BETS_FAILED:
			return {
				...state,
				pendingBetsState: PENDING_BET_STATE_PENDING
			};
		case PLACE_BETS_SUCCEEDED:
			return {
				...state,
				pendingBetsState: PENDING_BET_STATE_PLACED,
				betReceipts: payload
			};
		case BETSLIP_MINIMISED:
			return {
				...state,
				isBetslipExpanded: false
			};
		case BETSLIP_EXPANDED:
			return {
				...state,
				isBetslipExpanded: true
			};
		case BETSLIP_VALIDATED:
			return {
				...state,
				betslipErrors: payload.errors
			};
		case BETSLIP_MESSAGE_SHOWN:
			return {
				...state,
				betslipMessage: payload
			};
		case BETSLIP_MESSAGE_HIDDEN:
			return {
				...state,
				betslipMessage: null
			};
		case ONBOARDING_SHOWN:
			return { ...state, isOnboardingOpen: true };
		case ONBOARDING_HIDDEN:
			return { ...state, isOnboardingOpen: false };
		case ONBOARDING_DISABLED_UPDATED:
			return {
				...state,
				isOnboardingDisabled: payload.onboardingIsDisabled
			};
		case LOGOUT:
			return {
				...state,
				betslipMessage: null,
				betslipErrors: [],
				betReceipts: [],
				pendingBets: []
			};
		case NANOPLAYER_SCRIPTS_LOADED:
			return {
				...state,
				externalNanoplayerScriptHasLoaded: true
			};
		case VIDEO_MUTED:
			return {
				...state,
				isVideoMuted: true
			};
		case VIDEO_UNMUTED:
			return {
				...state,
				isVideoMuted: false
			};
		case FETCH_INFO_SCHEDULE_OFFLINE:
		case FETCH_INFO_FAILED: {
			return {
				...state,
				showServerOfflinePage: true
			};
		}
		case ACTIVE_BETSLIP_TAB_UPDATED:
			return {
				...state,
				activeBetslipTab: payload.activeTab,
				betslipMessage: null
			};
		case COMBO_BET_AMOUNTS_UPDATED:
			return {
				...state,
				comboBetAmounts: payload.comboBetAmounts,
				betslipMessage: null
			};
		case BETSLIP_CLEARED:
			return {
				...state,
				activeBetslipTab: BETSLIP_TAB_SINGLE,
				betReceipts: [],
				pendingBets: [],
				pendingBetsState: PENDING_BET_STATE_PENDING,
				comboBetAmounts: calculateTaxationAmounts(0, 1),
				betslipMessage: null
			};
		case FETCH_EVENTS_FOR_WEEK_REQUESTED:
		case FETCH_EVENTS_FOR_WEEK_FAILED:
		case FETCH_INFO_REQUESTED:
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export default reducer;
